import { IWorkingPattern } from '@nxsys/types/models/workingPattern';
import moment, { Moment } from 'moment';

// NX-4948: ported from the Old UI (nxsys-fe-payroll RegistrationInfo.jsx /
// GeneralInfo index.jsx) — the working pattern + payroll calculation dates
// the backend pro-rates a salaried employee against.

export type WorkingPatternOption = {
  label: string;
  value: number;
  fullData: IWorkingPattern;
};

export type WorkingPatternValues = {
  workingPattern: WorkingPatternOption | null;
  payrollCalculationStart: Moment | null;
  payrollCalculationEnd: Moment | null;
};

export const toWorkingPatternOption = (
  pattern?: IWorkingPattern | null,
): WorkingPatternOption | null =>
  pattern
    ? { label: pattern.name, value: pattern.id, fullData: pattern }
    : null;

/** The company's default pattern from a company record's pattern list. */
export const findDefaultWorkingPattern = (
  patterns?: IWorkingPattern[] | null,
): IWorkingPattern | null => patterns?.find((p) => p.isDefault) ?? null;

/**
 * The employee's own pattern only — null means they follow the company
 * default, which WorkingPatternFields shows on its own.
 */
export const getWorkingPatternInitialValues = (
  employee: any,
): WorkingPatternValues => ({
  workingPattern: toWorkingPatternOption(employee?.salaryWorkingPattern),
  payrollCalculationStart: employee?.payrollCalculationStart
    ? moment(employee.payrollCalculationStart)
    : null,
  payrollCalculationEnd: employee?.payrollCalculationEnd
    ? moment(employee.payrollCalculationEnd)
    : null,
});

const toDate = (value: Moment | null | undefined) =>
  value ? moment(value).format('YYYY-MM-DD') : null;

/** Create: send whatever is set. */
export const buildWorkingPatternCreateParams = (
  values: Partial<WorkingPatternValues>,
) => {
  const params: Record<string, any> = {};
  if (values.workingPattern?.value) {
    params.workingPatternId = Number(values.workingPattern.value);
  }
  if (values.payrollCalculationStart) {
    params.payrollCalculationStart = toDate(values.payrollCalculationStart);
  }
  if (values.payrollCalculationEnd) {
    params.payrollCalculationEnd = toDate(values.payrollCalculationEnd);
  }
  return params;
};

/** Update: only send what changed against the stored record. */
export const buildWorkingPatternUpdateParams = (
  values: Partial<WorkingPatternValues>,
  employee: any,
) => {
  const params: Record<string, any> = {};

  // The backend stores the company default as null, so keeping the default
  // on an employee without their own pattern is not a change.
  const storedId: number | null = employee?.salaryWorkingPattern?.id ?? null;
  const selected = values.workingPattern;
  const keepsDefault = !storedId && !!selected?.fullData?.isDefault;
  if (!keepsDefault && (selected?.value ?? null) !== storedId) {
    // null removes the employee's own pattern.
    params.workingPatternId = selected?.value ? Number(selected.value) : null;
  }

  // Send a date only when it changed; null clears the stored one.
  const start = toDate(values.payrollCalculationStart);
  if (start !== toDate(employee?.payrollCalculationStart)) {
    params.payrollCalculationStart = start;
  }
  const end = toDate(values.payrollCalculationEnd);
  if (end !== toDate(employee?.payrollCalculationEnd)) {
    params.payrollCalculationEnd = end;
  }
  return params;
};
